import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Stack} from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {DateTimePicker} from '@mui/x-date-pickers';
import moment from 'moment/moment';

const DateTime = ({
                      index,
                      componentId,
                      value,
                      onChange,
                      descriptionHtml,
                      onDescriptionHtmlChange,
                      commitDescriptionHtml,
                      enableValidation,
                      isEditMode,
                      isFormArchived,
                      readOnly,
                      placeholderValues,
                      accessLevel,
                      submitMode,
                      minDateTime,
                      maxDateTime,
                      error,
                      audience,
                      recipientType,
                      linkedToStudents,
                  }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const dateTimePickerRef = useRef(null);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback(
        (nextDescriptionHtml) => {
            setDraftDescriptionHtml(nextDescriptionHtml);
            onDescriptionHtmlChange(nextDescriptionHtml);
        },
        [onDescriptionHtmlChange]
    );

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const formatDateTime = (date) => {
        return moment(date).format('D MMM YYYY HH:mm');
    };

    const handleOpen = () => {
        setIsOpen(true);
        setTimeout(() => {
            if (dateTimePickerRef.current) {
                dateTimePickerRef.current.scrollIntoView({
                    block: 'center',
                    inline: 'nearest',
                });
            }
        }, 10);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />

                        <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            gap={3}
                            mb={2}
                        >
                            <RequiredCheckbox
                                enableValidation={enableValidation}
                                index={index}
                                onChange={onChange}
                                isFormArchived={isFormArchived}
                            />
                        </Stack>
                        <Stack flexDirection="row" gap={2} mb={2}>
                            <DateTimePicker
                                label="Min Date Time"
                                value={minDateTime ? moment(minDateTime) : null}
                                onChange={(date) =>
                                    onChange({
                                        index,
                                        minDateTime: date ? date.toDate() : undefined,
                                    })
                                }
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!error,
                                        helperText: error,
                                    },
                                }}
                            />
                            <DateTimePicker
                                label="Max Date Time"
                                value={maxDateTime ? moment(maxDateTime) : null}
                                onChange={(date) =>
                                    onChange({
                                        index,
                                        maxDateTime: date ? date.toDate() : undefined,
                                    })
                                }
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!error,
                                        helperText: error,
                                    },
                                }}
                            />
                        </Stack>
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={draftDescriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            submitMode={submitMode}
                        />
                        {!readOnly
                            ? (
                                <DateTimePicker
                                    key={componentId}
                                    ref={dateTimePickerRef}
                                    open={isOpen}
                                    onOpen={handleOpen}
                                    onClose={handleClose}
                                    value={value ? moment(value) : null}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!error,
                                            helperText: error
                                        },
                                        field: {readOnly: true},
                                    }}
                                    format="D MMM YYYY HH:mm"
                                    ampm={false}
                                    required={enableValidation}
                                    onChange={(newValue) => {
                                        onChange({
                                            id: componentId,
                                            value: newValue ? formatDateTime(newValue) : null,
                                        });
                                    }}
                                    onAccept={() => {
                                        setIsOpen(false);
                                    }}
                                    disabled={!submitMode}
                                    minDateTime={minDateTime ? moment(minDateTime) : undefined}
                                    maxDateTime={maxDateTime ? moment(maxDateTime) : undefined}
                                />
                            )
                            : (
                                <Typography
                                    sx={{
                                        width: {
                                            xs: '80vw',
                                            sm: '60vw',
                                            md: '45vw',
                                            lg: '45vw',
                                            xl: '45vw',
                                        },
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                    }}
                                >
                                    {value && formatDateTime(value)}
                                </Typography>
                            )}
                    </>
                )}
        </Box>
    );
};

DateTime.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func,
    commitDescriptionHtml: PropTypes.func,
    onChange: PropTypes.func,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    minDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    maxDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
};

export default DateTime;
